import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, apiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ clinic_name: "", contact_name: "", phone: "", email: "", password: "", fiscal_name: "", cif: "", fiscal_address: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Clínica registrada");
      navigate("/agenda");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#373E45] p-12">
        <Link to="/" data-testid="register-logo-dark-link" className="self-start"><Logo variant="dark" className="h-9" /></Link>
        <div>
          <h2 className="text-3xl font-bold text-white leading-tight">Únete a Veteo y<br />reserva en segundos.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Registra tu clínica veterinaria para solicitar ecografías y gestionar tus citas.</p>
        </div>
        <p className="text-slate-400 text-sm">© {new Date().getFullYear()} Veteo</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm py-8">
          <div className="lg:hidden mb-8"><Link to="/" data-testid="register-logo-link"><Logo className="h-10" /></Link></div>
          <h1 className="text-2xl font-extrabold text-[#373E45]">Registrar clínica</h1>
          <p className="text-[#64748B] text-sm mt-1 mb-8">Crea tu cuenta profesional.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="clinic" className="text-[#373E45] font-semibold">Nombre de la clínica</Label>
              <Input id="clinic" required value={form.clinic_name} onChange={set("clinic_name")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Clínica Veterinaria..." data-testid="register-clinic-input" />
            </div>
            <div>
              <Label htmlFor="contact" className="text-[#373E45] font-semibold">Persona de contacto</Label>
              <Input id="contact" required value={form.contact_name} onChange={set("contact_name")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Nombre y apellidos" data-testid="register-contact-input" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-[#373E45] font-semibold">Teléfono</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="600 000 000" data-testid="register-phone-input" />
            </div>
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-bold text-[#64748B] uppercase mb-3">Datos de facturación (opcional)</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="fiscal_name" className="text-[#373E45] font-semibold">Nombre fiscal</Label>
                  <Input id="fiscal_name" value={form.fiscal_name} onChange={set("fiscal_name")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Razón social" data-testid="register-fiscalname-input" />
                </div>
                <div>
                  <Label htmlFor="cif" className="text-[#373E45] font-semibold">CIF</Label>
                  <Input id="cif" value={form.cif} onChange={set("cif")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="B12345678" data-testid="register-cif-input" />
                </div>
                <div>
                  <Label htmlFor="fiscal_address" className="text-[#373E45] font-semibold">Dirección fiscal</Label>
                  <Input id="fiscal_address" value={form.fiscal_address} onChange={set("fiscal_address")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Calle, nº, CP, ciudad" data-testid="register-fiscaladdress-input" />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-[#373E45] font-semibold">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="clinica@email.com" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#373E45] font-semibold">Contraseña</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Mínimo 6 caracteres" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl h-11" data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Crear cuenta"}
            </Button>
          </form>
          <p className="text-sm text-[#64748B] mt-6 text-center">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-[#373E45] font-bold hover:underline" data-testid="goto-login-link">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
